import React from 'react';
import classNames from 'classnames';

import './loading-skeleton.scss';

type LoadingSkeletonAppearance = 'default' | 'strong' | 'light';

export interface LoadingSkeletonProps {
  width: number | string;
  height: number | string;
  appearance?: LoadingSkeletonAppearance;
  noMargin?: boolean;
  noRadius?: boolean;
  /** Disable the opacity-pulse css animation defined in skeleton-screen-bar.scss. */
  noAnimation?: boolean;
  /** An in-code hint for what the Skeleton is for, a test id, and an aria label. */
  label?: string;
}

const DEFAULT_MARGIN_BOTTOM_PX = 12;
const DEFAULT_BORDER_RADIUS_PX = 4;

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = (props) => {
  const {
    width,
    height,
    appearance = 'default',
    noMargin,
    noRadius,
    noAnimation,
    label = 'dragonfruit-loading-skeleton',
  } = props;
  return (
    <div
      className={classNames('dragonfruit-loading-skeleton', {
        [`dragonfruit-loading-skeleton--${appearance}`]: Boolean(appearance),
      })}
      style={{
        width,
        height,
        margin: noMargin ? undefined : `0 0 ${DEFAULT_MARGIN_BOTTOM_PX}px 0`,
        borderRadius: noRadius ? undefined : DEFAULT_BORDER_RADIUS_PX,
        animation: noAnimation ? undefined : 'loading 1.5s infinite linear',
      }}
      data-testid={label}
      aria-label={label}
    />
  );
};

interface LoadingSkeletonGroupProps {
  loadingSkeletonProps: LoadingSkeletonProps[];
}

/**
 * LoadingSkeletonGroup is a slightly easier interface for rendering
 * multiple loading skeletons in sequence.
 */
export const LoadingSkeletonGroup = ({
  loadingSkeletonProps,
}: LoadingSkeletonGroupProps): JSX.Element => {
  return (
    <>
      {loadingSkeletonProps.map((props, index) => (
        <LoadingSkeleton key={`dragonfruit-loading-skeleton-${index}`} {...props} />
      ))}
    </>
  );
};

export default LoadingSkeleton;
